import useAccountRepo from '@/composables/logged-in-user-account/account-repo';
import useAccountSlugs from '@/composables/logged-in-user-account/account-slug';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import { account as accountConfig } from 'shared/api/query/configs.json';
import { computed } from 'vue';
import { dogma } from '@/api';
import { isAccountDeletable, isAccountExpired } from '@/lib/account/deletable';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { uploadFile } from '@/lib/image/image';

export default function useLoggedInUserAccount() {
  const accountRepo = useAccountRepo();
  const { loggedInUser } = useLoggedInUser();
  const { slugs } = useAccountSlugs();

  const loggedInUserAccount = computed(() => {
    if (loggedInUser.value === null || loggedInUser.value.account === undefined) {
      return { accountSettings: {}, companyImage: {}, goalSettings: {}, customerContract: {} };
    }

    const account = accountRepo.selectSingle(loggedInUser.value.account.uid);
    if (account === undefined) {
      return { accountSettings: {}, companyImage: {}, goalSettings: {}, customerContract: {} };
    }

    return account;
  });

  const uploadImage = (blob) => uploadFile(blob, () => {}).then((data) => {
    const companyImage = () => {
      if (loggedInUserAccount.value.companyImage === null) {
        return { blob: data.fileId, getURL: data.getURL };
      }
      return {
        uid: loggedInUserAccount.value.companyImage.uid,
        blob: data.fileId,
        getURL: data.getURL,
      };
    };
    return accountRepo.updateSingle({
      uid: loggedInUserAccount.value.uid,
      companyImage: companyImage(),
    }, { optimistic: false });
  });

  const deleteImage = () => {
    if (loggedInUserAccount.value.companyImage === null) {
      return new Promise((resolve) => {
        resolve();
      });
    }
    return accountRepo.updateSingle({
      uid: loggedInUserAccount.value.uid,
      companyImage: null,
    }, { optimistic: false });
  };

  const updateSingle = (entity, options, hookParameter) => {
    if (!isNullOrUndefined(entity.activeSlug)) {
      const existingSlug = slugs.value.find((s) => s.slug === entity.activeSlug.slug);
      if (existingSlug !== undefined) {
        entity.activeSlug = existingSlug;
      }
    }
    return accountRepo.updateSingle(entity, options, hookParameter);
  };

  const isDeletable = computed(() => isAccountDeletable(
    loggedInUserAccount.value?.accountSettings,
    loggedInUserAccount.value?.customerContract,
  ));

  const isExpired = computed(() => isAccountExpired(loggedInUserAccount.value?.accountSettings));

  // Do not use nebula since then we get a lot of errors for account missing.
  // We reload which resets the store anyway.
  const deleteAccount = () => dogma.update(
    [{ uid: loggedInUserAccount.value.uid, deletedAt: new Date().toISOString() }],
    accountConfig.model,
    [],
  );

  return {
    loggedInUserAccount,
    isDeletable,
    isExpired,
    uploadImage,
    deleteImage,
    deleteAccount,
    updateSingle,
    getList: accountRepo.getList,
  };
}
